import { createContext, PropsWithChildren, useEffect, useCallback, useContext, useState, useMemo, Fragment } from "react"
import { ContextDisplayData, ContainerProps, ContainerTye, DisplayType } from "./type";


const DEFAULT: ContextDisplayData = {
    displayType: DisplayType.EXTRA_LARGE,
    isMobile: false
}

const DisplayContext = createContext<ContextDisplayData>(DEFAULT);

export const CustomContainer = ({ children, className, type, condition, ...props }: ContainerProps) => {
    const { isMobile, displayType } = useDisplay();

    const customClassName = useMemo(() => {
        let _className = className;
        if (isMobile)
            _className += ' mobile';
        else {
            switch (displayType) {
                case DisplayType.EXTRA_SMALL:
                default:
                    _className += ' xs'
                    break;
                case DisplayType.SMALL:
                    _className += ' sm'
                    break;
                case DisplayType.MEDIUM:
                    _className += ' md';
                    break;
                case DisplayType.LARGE:
                    _className += ' lg';
                    break;
                case DisplayType.EXTRA_LARGE:
                    _className += ' xl';
                    break;
            }
        }

        return _className
    }, [className, displayType, isMobile]);

    const htmlElment = useMemo(() => {
        const _condition = condition ? condition(isMobile, displayType) : true;
        if (!_condition)
            return null;

        switch (type) {
            case ContainerTye.div:
                return <div className={customClassName} {...props}>
                    {children}
                </div>
            case ContainerTye.aside:
                return <aside className={customClassName} {...props}>
                    {children}
                </aside>
            case ContainerTye.span:
                return <span className={customClassName} {...props}>
                    {children}
                </span>
            default:
                return <Fragment>
                    {children}
                </Fragment>
        }
    }, [children, condition, customClassName, displayType, isMobile, props, type]);

    return (
        htmlElment
    )

}

export const WebContainer = ({ children, className, ...props }: ContainerProps) => {
    return <CustomContainer condition={(isMobile) => !isMobile} className={className} {...props}>
        {children}
    </CustomContainer>
}

export const MobileContainer = ({ children, className, ...props }: ContainerProps) => {
    return <CustomContainer condition={(isMobile) => isMobile} className={className} {...props}>
        {children}
    </CustomContainer>
}


export const DisplayProvider = ({ children }: PropsWithChildren<any>) => {
    const [displayType, setDisplayType] = useState<DisplayType>(DisplayType.EXTRA_LARGE);
    const [isMobile, setIsMobile] = useState<boolean>(false);

    const getIsMobile = useCallback(() => {
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            setIsMobile(true);
        } else {
            setIsMobile(false);
        }
    }, []);

    const handleResize = useCallback(() => {
        getIsMobile();
        const displayWidth = window.innerWidth;
        let type: DisplayType = DisplayType.EXTRA_SMALL;
        if (displayWidth < 576)
            type = DisplayType.EXTRA_SMALL;
        else if (displayWidth < 768)
            type = DisplayType.SMALL;
        else if (displayWidth < 992)
            type = DisplayType.MEDIUM;
        else if (displayWidth < 1200)
            type = DisplayType.LARGE;
        else
            type = DisplayType.EXTRA_LARGE;
        setDisplayType(type);
    }, [getIsMobile]);

    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);


    return (
        <DisplayContext.Provider value={{
            displayType,
            isMobile
        }}>
            {children}
        </DisplayContext.Provider>
    )
}

export const useDisplay = (): ContextDisplayData => {
    const context = useContext(DisplayContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}